import React, { useState, useEffect, useCallback } from 'react';
import {
    Tag,
    Button,
    Space,
    Empty,
    Spin,
    App,
    Input,
    Typography,
    Divider,
} from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    DownOutlined,
    UpOutlined,
} from '@ant-design/icons';
import TagGroupService from '../../service/tagGroupService';
import UserPreferencesService, { CategoryTags } from '../../service/userPreferencesService';

const { Text } = Typography;

interface TagGroupDetailProps {
    groupId: number;
    groupName: string;
    onTagsChange?: () => void;
}

const TagGroupDetail: React.FC<TagGroupDetailProps> = ({ groupId, groupName, onTagsChange }) => {
    const { message } = App.useApp();
    const [tags, setTags] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [searchResults, setSearchResults] = useState<string[]>([]);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [popularTags, setPopularTags] = useState<CategoryTags[]>([]);
    const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

    const fetchGroupTags = useCallback(async () => {
        setLoading(true);
        try {
            const response = await TagGroupService.getTagGroupWithPreferences(groupId);
            const activeTags = response.preferences
                .filter(p => p.is_preferred)
                .map(p => p.tag);
            setTags(activeTags);
        } catch (error: any) {
            setTags([]);
        } finally {
            setLoading(false);
        }
    }, [groupId]);

    const fetchPopularTags = useCallback(async () => {
        try {
            const response = await UserPreferencesService.getPopularTags();
            setPopularTags(Array.isArray(response) ? response : []);
        } catch (error) {
            // Silent failure - popular tags are optional
        }
    }, []);

    useEffect(() => {
        fetchGroupTags();
        fetchPopularTags();
    }, [fetchGroupTags, fetchPopularTags]);

    const handleSearchTags = async (value: string) => {
        if (!value.trim()) {
            setSearchResults([]);
            return;
        }

        setSearchLoading(true);
        try {
            const results = await UserPreferencesService.searchTags(value);
            setSearchResults(results);
        } catch (error: any) {
            message.error('Failed to search tags');
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleAddTag = async (tag: string) => {
        try {
            await UserPreferencesService.addUserPreference({
                preference_type: 'tag',
                preference_value: tag,
                tag_group_id: groupId,
            });
            message.success(`Added "${tag}" to ${groupName}`);
            await fetchGroupTags();
            if (onTagsChange) onTagsChange();
        } catch (error: any) {
            message.error(error.message || 'Failed to add tag');
        }
    };

    const handleRemoveTag = async (tag: string) => {
        try {
            await UserPreferencesService.removeUserPreference('tag', tag);
            message.success(`Removed "${tag}" from ${groupName}`);
            await fetchGroupTags();
            if (onTagsChange) onTagsChange();
        } catch (error: any) {
            message.error(error.message || 'Failed to remove tag');
        }
    };

    const toggleCategory = (category: string) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category],
        }));
    };

    const isTagInGroup = (tag: string) => tags.includes(tag);

    return (
        <div style={{ padding: '8px 0' }}>
            {/* Current Tags */}
            <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: 13 }}>Tags in this group:</Text>
                <div style={{ marginTop: 8 }}>
                    {loading ? (
                        <Spin size="small" />
                    ) : tags.length > 0 ? (
                        <Space wrap size="small">
                            {tags.map(tag => (
                                <Tag
                                    key={tag}
                                    closable
                                    onClose={() => handleRemoveTag(tag)}
                                    color="blue"
                                    style={{ fontSize: 12, padding: '2px 8px' }}
                                >
                                    #{tag}
                                </Tag>
                            ))}
                        </Space>
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="No tags in this group yet"
                            style={{ margin: '8px 0' }}
                        />
                    )}
                </div>
            </div>

            <Divider style={{ margin: '12px 0' }} />

            {/* Add Tags Section */}
            <div>
                <Text strong style={{ fontSize: 13 }}>Add tags:</Text>
                <div style={{ marginTop: 8 }}>
                    <Input
                        placeholder="Search tags..."
                        prefix={searchLoading ? <Spin size="small" /> : <SearchOutlined />}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            handleSearchTags(e.target.value);
                        }}
                        allowClear
                        size="small"
                        style={{ marginBottom: 12 }}
                    />

                    {/* Search Results */}
                    {searchTerm && (
                        <div style={{ marginBottom: 12 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Search Results {searchResults.length > 0 && `(${searchResults.length})`}:
                            </Text>
                            <div style={{ marginTop: 4 }}>
                                {searchLoading ? (
                                    <div style={{ textAlign: 'center', padding: '24px 0' }}>
                                        <Spin size="small" />
                                        <div style={{ marginTop: 8 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Searching tags...
                                            </Text>
                                        </div>
                                    </div>
                                ) : searchResults.length > 0 ? (
                                    <Space wrap size="small">
                                        {searchResults.map(tag => (
                                            <Tag
                                                key={tag}
                                                style={{
                                                    cursor: isTagInGroup(tag) ? 'not-allowed' : 'pointer',
                                                    fontSize: 11,
                                                    opacity: isTagInGroup(tag) ? 0.5 : 1,
                                                }}
                                                color={isTagInGroup(tag) ? 'default' : 'green'}
                                                onClick={() => !isTagInGroup(tag) && handleAddTag(tag)}
                                                icon={!isTagInGroup(tag) ? <PlusOutlined /> : undefined}
                                            >
                                                #{tag}
                                            </Tag>
                                        ))}
                                    </Space>
                                ) : (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description={
                                            <span style={{ fontSize: 12 }}>
                                                No tags found for "{searchTerm}". Try a different search term.
                                            </span>
                                        }
                                        style={{ padding: '16px 0' }}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Popular Tags */}
                    {(!searchTerm || searchResults.length === 0) && popularTags.length > 0 && (
                        <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Popular tags by category:
                            </Text>
                            <div style={{ marginTop: 8 }}>
                                {popularTags.slice(0, 3).map(category => (
                                    <div key={category.category} style={{ marginBottom: 12 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                            <Text strong style={{ fontSize: 12 }}>{category.category}</Text>
                                            {category.tags.length > 5 && (
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    onClick={() => toggleCategory(category.category)}
                                                    icon={expandedCategories[category.category] ? <UpOutlined /> : <DownOutlined />}
                                                    style={{ fontSize: 11 }}
                                                >
                                                    {expandedCategories[category.category] ? 'Less' : 'More'}
                                                </Button>
                                            )}
                                        </div>
                                        <Space wrap size="small">
                                            {category.tags
                                                .slice(0, expandedCategories[category.category] ? undefined : 5)
                                                .map(tag => (
                                                    <Tag
                                                        key={tag}
                                                        style={{
                                                            cursor: isTagInGroup(tag) ? 'not-allowed' : 'pointer',
                                                            fontSize: 11,
                                                            opacity: isTagInGroup(tag) ? 0.5 : 1,
                                                        }}
                                                        color={isTagInGroup(tag) ? 'default' : 'green'}
                                                        onClick={() => !isTagInGroup(tag) && handleAddTag(tag)}
                                                        icon={!isTagInGroup(tag) ? <PlusOutlined /> : undefined}
                                                    >
                                                        #{tag}
                                                    </Tag>
                                                ))}
                                        </Space>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TagGroupDetail;
